/**
 * 术语建议组件
 *
 * 功能：
 * 1. 监听原文区域的文本选择
 * 2. 选中文本后显示浮动"添加术语"按钮
 * 3. 弹出表单填写目标术语、上下文等
 * 4. 提交后进入术语候选（待PM审核）
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { termsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Plus, X } from 'lucide-react'
import { toast } from 'sonner'

interface TermSuggestionPopoverProps {
  projectId: string
  clientId: string
  containerRef: React.RefObject<HTMLDivElement | null>
}

export function TermSuggestionPopover({ projectId, clientId, containerRef }: TermSuggestionPopoverProps) {
  const queryClient = useQueryClient()
  const [selectedText, setSelectedText] = useState('')
  const [buttonPosition, setButtonPosition] = useState<{ x: number; y: number } | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [targetText, setTargetText] = useState('')
  const [context, setContext] = useState('')
  const buttonRef = useRef<HTMLButtonElement>(null)

  const suggestMutation = useMutation({
    mutationFn: (data: {
      projectId: string
      clientId: string
      sourceText: string
      targetText: string
      context?: string
    }) => termsApi.suggest(data),
    onSuccess: () => {
      toast.success('术语建议已提交，等待PM审核')
      setDialogOpen(false)
      setSelectedText('')
      setButtonPosition(null)
      setTargetText('')
      setContext('')
      queryClient.invalidateQueries({ queryKey: ['term-candidates', projectId] })
    },
    onError: (error: any) => {
      toast.error(error.message || '提交失败')
    },
  })

  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed) {
      setSelectedText('')
      setButtonPosition(null)
      return
    }

    const text = selection.toString().trim()
    if (!text || text.length < 1) {
      setSelectedText('')
      setButtonPosition(null)
      return
    }

    // 检查选择是否在 containerRef 内
    const container = containerRef.current
    if (!container) return

    const range = selection.getRangeAt(0)
    if (!container.contains(range.commonAncestorContainer)) {
      setSelectedText('')
      setButtonPosition(null)
      return
    }

    const rect = range.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    setSelectedText(text)
    setButtonPosition({
      x: rect.left - containerRect.left + rect.width / 2,
      y: rect.top - containerRect.top - 40,
    })
  }, [containerRef])

  const handleSubmit = () => {
    if (!targetText.trim()) {
      toast.error('请填写目标术语')
      return
    }

    suggestMutation.mutate({
      projectId,
      clientId,
      sourceText: selectedText,
      targetText: targetText.trim(),
      context: context.trim() || undefined,
    })
  }

  // 监听选择事件
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('mouseup', handleTextSelection)
    return () => {
      container.removeEventListener('mouseup', handleTextSelection)
    }
  }, [containerRef, handleTextSelection])

  // 点击外部关闭按钮
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        // 如果不是在选择文本，则关闭按钮
        const selection = window.getSelection()
        if (!selection || selection.isCollapsed) {
          setButtonPosition(null)
          setSelectedText('')
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <>
      {/* 浮动添加按钮 */}
      {buttonPosition && !dialogOpen && (
        <button
          ref={buttonRef}
          className="absolute z-50 flex items-center gap-1 px-2.5 py-1.5 bg-primary text-primary-foreground text-xs rounded-md shadow-lg hover:bg-primary/90 transition-colors animate-in fade-in zoom-in-95 duration-150"
          style={{
            left: `${buttonPosition.x}px`,
            top: `${buttonPosition.y}px`,
            transform: 'translateX(-50%)',
          }}
          onClick={() => setDialogOpen(true)}
        >
          <Plus className="w-3 h-3" />
          添加术语
        </button>
      )}

      {/* 提交表单 Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">添加术语建议</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">源术语</Label>
              <div className="px-3 py-2 bg-muted rounded-md text-sm font-medium">
                {selectedText}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="target-text" className="text-xs">
                目标术语 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="target-text"
                value={targetText}
                onChange={(e) => setTargetText(e.target.value)}
                placeholder="请输入目标语言术语"
                className="h-9"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="context" className="text-xs">上下文/备注（可选）</Label>
              <Input
                id="context"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="例如：仅在技术文档中使用"
                className="h-9"
              />
            </div>

          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDialogOpen(false)
                setTargetText('')
                setContext('')
              }}
            >
              <X className="w-3.5 h-3.5 mr-1" />
              取消
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={suggestMutation.isPending || !targetText.trim()}
            >
              {suggestMutation.isPending ? '提交中...' : '提交建议'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
